/**
 * 展示层格式化。只依赖 Intl，浏览器与 Worker 通用。
 * 时区固定按浏览器本地时区渲染 —— 控制台的使用者是客户自己人，不是运维。
 */

const DATE_TIME = new Intl.DateTimeFormat('zh-CN', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const DATE_ONLY = new Intl.DateTimeFormat('zh-CN', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

const CURRENCY_FORMATTERS = new Map<string, Intl.NumberFormat>();

/**
 * 金额。入参一律是**最小货币单位的整数**（分），后端到前端都是这个口径。
 *
 * 这里刻意用 `Intl` 在**运行时**渲染货币符号与小数位，而不是拼一段含符号的文案：
 * 构建产物里因此不会出现任何写死的金额 —— 这是控制台能展示真实金额的前提，
 * 见 `verify-compliance.mjs` 里「不得出现具体金额」那条断言。
 */
export function formatMoney(
  minor: number | null | undefined,
  currency = 'CNY',
  fallback = '—',
): string {
  if (minor === null || minor === undefined || !Number.isFinite(minor)) return fallback;

  let formatter = CURRENCY_FORMATTERS.get(currency);
  if (!formatter) {
    formatter = new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    CURRENCY_FORMATTERS.set(currency, formatter);
  }
  return formatter.format(minor / 100);
}

/** `2026/10/07 20:31`；输入非法时返回占位符而不是 `Invalid Date`。 */
export function formatDateTime(iso: string | null | undefined, fallback = '—'): string {
  const date = parse(iso);
  return date ? DATE_TIME.format(date) : fallback;
}

export function formatDate(iso: string | null | undefined, fallback = '—'): string {
  const date = parse(iso);
  return date ? DATE_ONLY.format(date) : fallback;
}

/**
 * 相对时间。超过 30 天就退回绝对时间 —— 「87 天前」并不比日期更好读。
 */
export function formatRelative(iso: string | null | undefined, now = Date.now(), fallback = '—'): string {
  const date = parse(iso);
  if (!date) return fallback;

  const diffMs = now - date.getTime();
  const future = diffMs < 0;
  const abs = Math.abs(diffMs);
  const minutes = Math.floor(abs / 60_000);

  if (minutes < 1) return '刚刚';
  if (minutes < 60) return future ? `${minutes} 分钟后` : `${minutes} 分钟前`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return future ? `${hours} 小时后` : `${hours} 小时前`;

  const days = Math.floor(hours / 24);
  if (days <= 30) return future ? `${days} 天后` : `${days} 天前`;

  return formatDate(iso, fallback);
}

/** 剩余有效期，用于会话列表。 */
export function formatRemaining(iso: string | null | undefined, now = Date.now(), fallback = '—'): string {
  const date = parse(iso);
  if (!date) return fallback;
  const diffMs = date.getTime() - now;
  if (diffMs <= 0) return '已过期';

  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return `剩 ${Math.max(1, minutes)} 分钟`;

  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `剩 ${hours} 小时`;

  return `剩 ${Math.floor(hours / 24)} 天`;
}

/**
 * 把 User-Agent 压成「浏览器 · 系统」。
 * 只做粗识别：这里的目标是让用户认出「是不是我自己的设备」，不是做指纹。
 */
export function describeUserAgent(userAgent: string | null | undefined): string {
  if (!userAgent) return '未知设备';
  const ua = userAgent.toLowerCase();

  const browser = ua.includes('edg/')
    ? 'Edge'
    : ua.includes('chrome/') && !ua.includes('chromium')
      ? 'Chrome'
      : ua.includes('firefox/')
        ? 'Firefox'
        : ua.includes('safari/') && ua.includes('version/')
          ? 'Safari'
          : '浏览器';

  const os = ua.includes('windows')
    ? 'Windows'
    : ua.includes('android')
      ? 'Android'
      : ua.includes('iphone') || ua.includes('ipad')
        ? 'iOS'
        : ua.includes('mac os x')
          ? 'macOS'
          : ua.includes('linux')
            ? 'Linux'
            : '未知系统';

  return `${browser} · ${os}`;
}
